import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  alpha,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  styled,
  Tooltip,
  Typography
} from '@mui/material';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import DoneAllTwoToneIcon from '@mui/icons-material/DoneAllTwoTone';
import VolumeOffTwoToneIcon from '@mui/icons-material/VolumeOffTwoTone';
import VolumeUpTwoToneIcon from '@mui/icons-material/VolumeUpTwoTone';
import { formatDistanceToNow, isValid } from 'date-fns';

import AuthContext from 'src/contexts/FirebaseAuthContext';
import { useSelector } from 'src/store';
import {
  enqueueNotificationToast,
  notificationToastDetails,
  openConversationNotification,
  rememberNotification,
  teamMessageNotification
} from 'src/utils/incomingMessageNotifications';
import { socketClient } from 'src/index';
import { buildApiUrl } from 'src/utils/api';
import firebase from 'src/utils/firebase';
import {
  notificationExcerpt,
  shouldSilenceConversationNotification
} from 'src/utils/notificationPresentation';
import { activeShopHeaders } from 'src/utils/shopContext';
import NotificationToasts from './NotificationToasts';

const SOUND_STORAGE_KEY = 'autoclever.notificationSoundEnabled';
let notificationAudioContext = null;

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
      background: ${theme.palette.error.main};
      color: ${theme.palette.error.contrastText};
      min-width: 17px;
      height: 17px;
      padding: 0 4px;
      font-size: 10px;
      box-shadow: 0 0 0 2px ${theme.palette.background.paper};
    }
  `
);

const typeColor = (type, theme) => {
  if (type === 'sms' || type === 'live_chat') return theme.palette.info.main;
  if (type === 'finance_lead' || type === 'test_drive') return theme.palette.success.main;
  if (type === 'jarvis') return theme.palette.primary.main;
  return theme.palette.warning.main;
};

const playNotificationTone = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  notificationAudioContext = notificationAudioContext || new AudioContext();
  const context = notificationAudioContext;
  if (context.state === 'suspended') {
    context.resume().catch(() => {});
  }
  const now = context.currentTime;
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.16, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.72);
  gain.connect(context.destination);

  [659.25, 523.25].forEach((frequency, index) => {
    const oscillator = context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, now + index * 0.13);
    oscillator.connect(gain);
    oscillator.start(now + index * 0.13);
    oscillator.stop(now + 0.72);
  });
};

const getAuthConfig = async () => {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) throw new Error('Authentication required.');
  const idToken = await currentUser.getIdToken();
  return {
    idToken,
    config: { headers: { Authorization: `Bearer ${idToken}`, ...activeShopHeaders() } }
  };
};

const persistNotificationRead = async (notificationId) => {
  const { config } = await getAuthConfig();
  await axios.post(buildApiUrl(`/api/notifications/${notificationId}/read`), {}, config);
};

function HeaderNotifications() {
  const anchorRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const announcedIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const loadGenerationRef = useRef(0);
  const lastToneRef = useRef(0);
  const soundEnabledRef = useRef(true);
  const { user } = useContext(AuthContext);
  const socketStatus = useSelector((state) => state.socket.connectionStatus);
  const scopeKey = `${user?.id || ''}:${user?.shop?.id || ''}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const actorKey = user?.employeeId ? `employee:${user.employeeId}`
    : ['owner', 'admin'].includes(user?.role) ? 'tower' : null;
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(
    () => window.localStorage.getItem(SOUND_STORAGE_KEY) !== 'false'
  );

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    const unlockAudio = () => {
      if (!soundEnabledRef.current) return;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      notificationAudioContext = notificationAudioContext || new AudioContext();
      if (notificationAudioContext.state === 'suspended') {
        notificationAudioContext.resume().catch(() => {});
      }
    };
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const dismissToast = useCallback((key) => {
    setToasts((current) => current.filter((item) => item.key !== key));
  }, []);

  const announceNotification = useCallback((notification) => {
    const key = notificationToastDetails(notification).key;
    if (!rememberNotification(announcedIdsRef.current, key) ||
        notification.readAt || shouldSilenceConversationNotification(notification)) return;
    setToasts((current) => enqueueNotificationToast(current, notification));
    if (soundEnabledRef.current && Date.now() - lastToneRef.current > 750) {
      lastToneRef.current = Date.now();
      playNotificationTone();
    }
  }, []);

  const loadNotifications = useCallback(async ({ quiet = false, announceNew = false } = {}) => {
    if (!firebase.auth().currentUser || scopeRef.current !== scopeKey) return;
    const generation = ++loadGenerationRef.current;
    const currentScope = () => scopeRef.current === scopeKey && generation === loadGenerationRef.current;
    if (!quiet) setLoading(true);
    try {
      const { config } = await getAuthConfig();
      if (!currentScope()) return;
      const response = await axios.get(buildApiUrl('/api/notifications'), config);
      if (!currentScope()) return;
      const nextItems = Array.isArray(response.data?.items) ? response.data.items : [];
      const silentlyReadIds = new Set(nextItems
        .filter((item) => !item.readAt && shouldSilenceConversationNotification(item))
        .map((item) => Number(item.id))
        .filter(Boolean));
      const silentReadAt = silentlyReadIds.size ? new Date().toISOString() : null;
      const presentedItems = nextItems.map((item) => (
        silentlyReadIds.has(Number(item.id)) ? { ...item, readAt: silentReadAt } : item
      ));
      const newlyDiscovered = announceNew && initializedRef.current
        ? presentedItems.filter((item) => !item.readAt && !knownIdsRef.current.has(Number(item.id)))
        : [];
      nextItems.forEach((item) => rememberNotification(knownIdsRef.current, Number(item.id)));
      initializedRef.current = true;
      setItems(presentedItems);
      setUnreadCount(Math.max(0, Number(response.data?.unreadCount || 0) - silentlyReadIds.size));
      setError('');
      if (silentlyReadIds.size) {
        Promise.all(Array.from(silentlyReadIds).map((id) => persistNotificationRead(id)))
          .catch(() => undefined);
      }
      newlyDiscovered.slice(0, 20).reverse().forEach(announceNotification);
    } catch (requestError) {
      if (!quiet && currentScope()) {
        setError(requestError?.response?.data?.error || requestError?.message || 'Unable to load notifications.');
      }
    } finally {
      if (currentScope()) setLoading(false);
    }
  }, [scopeKey, announceNotification]);

  useEffect(() => {
    knownIdsRef.current.clear();
    announcedIdsRef.current.clear();
    initializedRef.current = false;
    setItems([]);
    setToasts([]);
    setUnreadCount(0);
    setError('');
    setOpen(false);
    if (!user?.id) return undefined;
    loadNotifications();
    const poll = window.setInterval(
      () => loadNotifications({ quiet: true, announceNew: true }), 60000
    );
    return () => {
      loadGenerationRef.current += 1;
      window.clearInterval(poll);
    };
  }, [loadNotifications, user?.id]);

  useEffect(() => {
    const socket = socketClient.socket;
    if (!user?.id || socketStatus !== 'connected' || !socket) return undefined;
    let disposed = false;
    let subscribed = false;
    let subscribing = false;
    const currentScope = () => !disposed && scopeRef.current === scopeKey && socketClient.socket === socket;
    const handleNotification = (notification) => {
      if (!currentScope() || !notification?.id) return;
      const notificationId = Number(notification.id);
      if (!rememberNotification(knownIdsRef.current, notificationId)) return;
      const silentlyRead = !notification.readAt && shouldSilenceConversationNotification(notification);
      const presentedNotification = silentlyRead
        ? { ...notification, readAt: new Date().toISOString() } : notification;
      setItems((current) => [presentedNotification, ...current].slice(0, 100));
      if (!notification.readAt && !silentlyRead) {
        setUnreadCount((current) => current + 1);
        announceNotification(notification);
      } else if (silentlyRead) {
        persistNotificationRead(notificationId).catch(() => {
          if (currentScope()) loadNotifications({ quiet: true });
        });
      }
    };
    const handleTeamMessage = (payload) => {
      if (!currentScope()) return;
      const notification = teamMessageNotification(payload, actorKey);
      if (notification) announceNotification(notification);
    };
    const subscribeRoom = async () => {
      if (!currentScope() || !socket.connected || subscribed || subscribing) return;
      subscribing = true;
      try {
        const { idToken } = await getAuthConfig();
        if (!currentScope()) return;
        await new Promise((resolve, reject) => {
          socket.timeout(8000).emit('notifications:subscribe', { idToken }, (error, response) => {
            if (error || response?.error || !response?.ok) reject(error || new Error('Subscription unavailable'));
            else resolve(response);
          });
        });
        if (currentScope()) subscribed = true;
      } catch (_error) {
        // Polling recovers customer notifications; retry a failed subscription.
      } finally {
        subscribing = false;
      }
    };
    const onConnect = () => { subscribed = false; subscribeRoom(); };
    socket.on('shop:notification', handleNotification);
    socket.on('team:message:new', handleTeamMessage);
    socket.on('connect', onConnect);
    subscribeRoom();
    const retry = window.setInterval(subscribeRoom, 20000);
    return () => {
      disposed = true;
      window.clearInterval(retry);
      socket.off('shop:notification', handleNotification);
      socket.off('team:message:new', handleTeamMessage);
      socket.off('connect', onConnect);
    };
  }, [actorKey, announceNotification, loadNotifications, scopeKey, socketStatus, user?.id]);

  const markRead = async (notification) => {
    if (!notification?.id || notification.readAt) return;
    const readAt = new Date().toISOString();
    setItems((current) => current.map((item) => (
      Number(item.id) === Number(notification.id) ? { ...item, readAt } : item
    )));
    setUnreadCount((current) => Math.max(0, current - 1));
    try {
      await persistNotificationRead(notification.id);
    } catch (requestError) {
      loadNotifications({ quiet: true });
    }
  };

  const openNotification = (notification) => {
    const details = notificationToastDetails(notification);
    setToasts((current) => current.filter((item) => item.key !== details.key &&
      (!details.conversationKey || item.conversationKey !== details.conversationKey)));
    setOpen(false);
    // Open immediately: an unavailable read-receipt endpoint must not block chat.
    if (!openConversationNotification(notification) && String(notification.actionUrl || '').startsWith('/app/')) {
      navigate(notification.actionUrl);
    }
    markRead(notification);
  };

  const markAllRead = async () => {
    if (!unreadCount) return;
    const readAt = new Date().toISOString();
    setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt || readAt })));
    setUnreadCount(0);
    try {
      const { config } = await getAuthConfig();
      await axios.post(buildApiUrl('/api/notifications/read-all'), {}, config);
    } catch (requestError) {
      loadNotifications({ quiet: true });
    }
  };

  const toggleSound = () => {
    setSoundEnabled((current) => {
      const next = !current;
      window.localStorage.setItem(SOUND_STORAGE_KEY, String(next));
      if (next) playNotificationTone();
      return next;
    });
  };

  return (
    <>
      <Tooltip arrow title="Notifications">
        <Button
          ref={anchorRef}
          onClick={() => setOpen(true)}
          color="primary"
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          sx={{
            minWidth: 40,
            height: 40,
            px: { xs: 1, lg: 1.25 },
            gap: 0.75,
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 700
          }}
        >
          <NotificationsBadge badgeContent={unreadCount} max={99} invisible={!unreadCount}>
            <NotificationsActiveTwoToneIcon fontSize="small" />
          </NotificationsBadge>
          <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
            Notifications
          </Box>
        </Button>
      </Tooltip>
      <Popover
        disableScrollLock
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: 'calc(100vw - 24px)', mt: 1 } }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.25}>
          <Box>
            <Typography variant="h5">Notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount ? `${unreadCount} unread` : 'You are caught up'}
            </Typography>
          </Box>
          <Box display="flex" gap={0.5}>
            <Tooltip title={soundEnabled ? 'Mute notification sound' : 'Enable notification sound'}>
              <IconButton size="small" onClick={toggleSound}>
                {soundEnabled ? <VolumeUpTwoToneIcon fontSize="small" /> : <VolumeOffTwoToneIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Mark all as read">
              <span>
                <IconButton size="small" onClick={markAllRead} disabled={!unreadCount}>
                  <DoneAllTwoToneIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Divider />
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress size={24} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>
        ) : !items.length ? (
          <Box px={2} py={5} textAlign="center">
            <Typography color="text.secondary">No notifications yet.</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 440, overflowY: 'auto' }}>
            {items.map((notification, index) => (
              <Box key={notification.id}>
                <ListItemButton
                  onClick={() => openNotification(notification)}
                  sx={(theme) => ({
                    alignItems: 'flex-start',
                    gap: 1.25,
                    px: 2,
                    py: 1.25,
                    backgroundColor: notification.readAt
                      ? 'transparent'
                      : alpha(typeColor(notification.type, theme), 0.06)
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      width: 8,
                      height: 8,
                      mt: 0.75,
                      flexShrink: 0,
                      borderRadius: '50%',
                      backgroundColor: notification.readAt
                        ? theme.palette.grey[400]
                        : typeColor(notification.type, theme)
                    })}
                  />
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <>
                        {notification.message && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              mt: 0.25,
                              overflow: 'hidden',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2
                            }}
                          >
                            {notificationExcerpt(notification.message)}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.disabled">
                          {isValid(new Date(notification.createdAt))
                            ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true }) : 'Just now'}
                        </Typography>
                      </>
                    }
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: notification.readAt ? 500 : 700,
                      noWrap: true
                    }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
                {index < items.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Popover>
      <NotificationToasts items={toasts} onOpen={openNotification} onDismiss={dismissToast} />
    </>
  );
}

export default HeaderNotifications;
